import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Plus, MoreVertical, Eye, Pencil, Trash2, Building2, Search, RefreshCw, FileUp } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getUserProperties, deleteProperty, getProperties } from "@/api/properties";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Definimos una interfaz para las propiedades que necesitamos
interface Property {
  _id: string;
  title: string;
  description: string;
  price: number;
  type: string;
  status: string;
  createdAt: string;
  views?: number;
  leads?: number;
  userId?: string;
  location: string | {
    address: string;
    city: string;
    state: string;
    country: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
}

export function PropertiesPage({ adminMode = false }: { adminMode?: boolean }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [propertyToDelete, setPropertyToDelete] = useState<Property | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchProperties = async () => {
    if (!user) {
      toast("Debes iniciar sesión para ver tus propiedades");
      navigate("/login");
      return;
    }

    if (adminMode && user.role !== 'admin') {
      toast("No tienes permisos para acceder a esta sección");
      navigate("/dashboard");
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      console.log(adminMode ? "Obteniendo todas las propiedades (Modo Admin)..." : "Obteniendo propiedades del usuario...");
      const data = adminMode ? await getProperties() : await getUserProperties();
      console.log("Propiedades obtenidas:", data);
      setProperties(data);
    } catch (err) {
      console.error("Error fetching properties:", err);
      const errorMessage = err instanceof Error ? err.message : "Error desconocido";
      setError(`Error al cargar las propiedades: ${errorMessage}`);
      toast("Error al cargar las propiedades");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, [user, navigate, adminMode]);

  const handleDeleteProperty = async () => {
    if (!propertyToDelete || !propertyToDelete._id) return;
    
    setIsDeleting(true);
    try {
      await deleteProperty(propertyToDelete._id);
      toast("Propiedad eliminada con éxito");
      // Actualizar la lista de propiedades
      setProperties(properties.filter(p => p._id !== propertyToDelete._id));
    } catch (error) {
      console.error("Error deleting property:", error);
      toast("Error al eliminar la propiedad");
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setPropertyToDelete(null);
    }
  };

  const openDeleteDialog = (property: Property) => {
    setPropertyToDelete(property);
    setIsDeleteDialogOpen(true);
  };

  const filteredProperties = properties.filter(property => {
    // Filtrar por estado
    if (activeTab !== "all" && property.status !== activeTab) {
      return false;
    }

    // Filtrar por búsqueda
    if (searchInput) {
      const searchTerm = searchInput.toLowerCase();
      return (
        property.title.toLowerCase().includes(searchTerm) ||
        property.description.toLowerCase().includes(searchTerm) ||
        (typeof property.location === 'string' && property.location.toLowerCase().includes(searchTerm)) ||
        (typeof property.location === 'object' && property.location.address.toLowerCase().includes(searchTerm))
      );
    }

    return true;
  });

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "active":
      case "available":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
      case "available":
        return "Activa";
      case "pending":
        return "Pendiente";
      case "inactive":
        return "Inactiva";
      default:
        return status;
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{adminMode ? 'Todas las Propiedades' : 'Mis Propiedades'}</h1>
          <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchProperties} 
            disabled={isLoading}
            className="flex items-center gap-1"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          {(user?.role === 'admin' || user?.role === 'broker') && (
            <Button 
              variant="outline" 
              onClick={() => navigate("/dashboard/admin/bulk-upload")}
              className="flex items-center gap-1"
            >
              <FileUp className="h-4 w-4" />
              Carga Masiva
            </Button>
          )}
          {!adminMode && (
            <Button onClick={() => navigate("/publicar")}>
              <Plus className="mr-2 h-4 w-4" />
              Nueva Propiedad
            </Button>
          )}
        </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{adminMode ? 'Gestión Global de Propiedades' : 'Propiedades'}</CardTitle>
            <CardDescription>
              <div className="flex justify-between items-center mt-2">
                <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger value="all">Todas</TabsTrigger>
                    <TabsTrigger value="active">Activas</TabsTrigger>
                    <TabsTrigger value="pending">Pendientes</TabsTrigger>
                    <TabsTrigger value="inactive">Inactivas</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar propiedades..."
                    className="pl-8 w-[250px]"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="text-red-500 mb-4">{error}</div>
                <Button variant="outline" onClick={fetchProperties}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Intentar de nuevo
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Propiedad</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Precio</TableHead>
                      {adminMode && <TableHead>Usuario</TableHead>}
                      <TableHead>Publicación</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProperties.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={adminMode ? 7 : 6} className="text-center py-8 text-muted-foreground">
                          No se encontraron propiedades
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProperties.map((property) => (
                        <TableRow key={property._id}>
                          <TableCell className="font-medium">
                            <div className="max-w-[200px] truncate" title={property.title}>
                              {property.title}
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">{property.type}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${getStatusBadgeColor(property.status || 'inactive')}`}>
                              {getStatusText(property.status || 'inactive')}
                            </span>
                          </TableCell>
                          <TableCell>${property.price.toLocaleString()}</TableCell>
                          {adminMode && (
                            <TableCell className="text-xs text-muted-foreground truncate max-w-[100px]" title={property.userId}>
                              {property.userId || 'N/A'}
                            </TableCell>
                          )}
                          <TableCell>{property.createdAt ? new Date(property.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => navigate(`/property/${property._id}`)}
                                  className="cursor-pointer"
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  <span>Ver</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => navigate(`/dashboard/properties/${property._id}/edit`)}
                                  className="cursor-pointer"
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  <span>Editar</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="cursor-pointer text-red-600"
                                  onClick={() => openDeleteDialog(property)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  <span>Eliminar</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará permanentemente la propiedad "{propertyToDelete?.title}".
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteProperty} 
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Eliminando...
                </>
              ) : (
                "Eliminar"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
} 